// src/Service/fetchDeployments.js
import axios from "axios";
import store from '@/store';

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
const DEFAULT_DEPLOYMENT = {
  title: "Tasman_Bay 119",
  label: "Tasman Bay (TASCAM Buoy)",
  disabled: false
};

// Log the API endpoint being used
console.log('API Endpoint:', apiEndpoint);

export const api = axios.create({
  baseURL: apiEndpoint,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Add request interceptor to automatically add the token
api.interceptors.request.use(
  async (config) => {
    const token = store.getters.getAuthToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Adding token to request:', config.url);
      console.log('Request headers:', {
        ...config.headers,
        Authorization: 'Bearer [HIDDEN]'
      });
      console.log('Request body:', config.data);
    } else {
      console.warn('No token available for request:', config.url);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', {
      message: error.message,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token errors
api.interceptors.response.use(
  (response) => {
    console.log(`Response from ${response.config.url}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: typeof response.data === 'object' ? 'data present' : 'no data'
    });
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized error:', {
        url: error.config?.url,
        message: error.message
      });
      await store.dispatch('clearAuth');
      window.location.href = '/';
    } else if (error.response?.status === 500) {
      console.error('Server error:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: {
          ...error.config?.headers,
          Authorization: 'Bearer [HIDDEN]'
        },
        data: error.response?.data
      });
    } else {
      console.error('API error:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Fetches and processes deployments from the API
 * @returns {Promise<Array>} Array of deployments with site and label
 */
export async function fetchDeployments() {
  try {
    console.log('Fetching deployments...');
    const response = await api.get('/Deployments');
    
    // Extract CAW_OOS deployments from the response
    const sources = response.data.sources;
    const cawOosSource = sources.find(source => 'CAW_OOS' in source);
    let deployments = cawOosSource ? cawOosSource.CAW_OOS : [];
    
    // Ensure TASCAM1 119 is always included if it's not already present
    // const hasDefaultDeployment = deployments.some(d => d.title === DEFAULT_DEPLOYMENT.title);
    // if (!hasDefaultDeployment) {
    //   deployments = [DEFAULT_DEPLOYMENT, ...deployments];
    //   console.log('Added default deployment TASCAM1 119');
    // }

    // Filter out disabled deployments
    deployments = deployments.filter(d => !d.disabled);
    
    console.log('Deployments processed:', {
      status: response.status,
      totalDeployments: deployments.length,
      defaultDeploymentAvailable: false,
      deployments: deployments.map(d => ({
        site: d.site,
        label: d.label
      }))
    });
    
    return deployments;
  } catch (error) {
    console.error("Error fetching deployments:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    // If we can't fetch deployments, return just the default deployment
    console.log('Returning default deployment only');
    return [DEFAULT_DEPLOYMENT];
  }
}

/**
 * Tests wind data retrieval for a specific deployment
 * @param {string} deploymentSite - The deployment site to test
 * @returns {Promise<boolean>} Whether the test was successful
 */
export async function testWindData(deploymentSite) {
  try {
    console.log('Testing wind data retrieval for deployment:', deploymentSite);
    const response = await api.post('/windData', {
      site: deploymentSite,
      depth: 0
    });
    console.log('Wind data test successful:', {
      status: response.status,
      hasData: !!response.data
    });
    return true;
  } catch (error) {
    console.error("Wind data test failed:", {
      message: error.message,
      status: error.response?.status,
      deploymentSite
    });
    return false;
  }
}

/**
 * Validates if a deployment exists
 * @param {string} deploymentSite - The deployment site to validate
 * @returns {Promise<boolean>} Whether the deployment exists
 */
export async function validateDeployment(deploymentSite) {
  if (deploymentSite === DEFAULT_DEPLOYMENT.site) {
    return true;
  }
  
  try {
    const deployments = await fetchDeployments();
    return deployments.some(d => d.site === deploymentSite);
  } catch (error) {
    console.error('Deployment validation failed:', error);
    // If validation fails and we're checking the default deployment, return true
    return deploymentSite === DEFAULT_DEPLOYMENT.site;
  }
}

/**
 * Tests if the current token is valid
 * @returns {Promise<boolean>} Whether the token is valid
 */
export async function testToken() {
  try {
    const token = store.getters.getAuthToken;
    if (!token) {
      console.warn('No token available for token test');
      return false;
    }
    console.log('Testing token validity...');
    const response = await api.get('/Deployments');
    console.log('Token test successful:', response.status === 200);
    return true;
  } catch (error) {
    console.error("Token test failed:", {
      message: error.message,
      status: error.response?.status
    });
    return false;
  }
}

/**
 * Checks if the API is healthy and responsive
 * @returns {Promise<boolean>} Whether the API is healthy
 */
export async function checkApiHealth() {
  try {
    const startTime = Date.now();
    const response = await api.get('/v2/deployments');
    const endTime = Date.now();
    console.log('API health check:', {
      status: response.status,
      responseTime: `${endTime - startTime}ms`,
      endpoint: apiEndpoint
    });
    return true;
  } catch (error) {
    console.error('API health check failed:', {
      message: error.message,
      endpoint: apiEndpoint
    });
    return false;
  }
}

/**
 * Gets deployment details whether from API or default
 * @param {string} deploymentSite - The deployment site to get details for
 * @returns {Promise<Object|null>} The deployment details or null if not found
 */
export async function getDeploymentDetails(deploymentSite) {
  try {
    // If it's the default deployment and we can't reach the API, return default
    if (deploymentSite === DEFAULT_DEPLOYMENT.site) {
      return DEFAULT_DEPLOYMENT;
    }
    
    const deployments = await fetchDeployments();
    return deployments.find(d => d.site === deploymentSite) || null;
  } catch (error) {
    console.error('Error getting deployment details:', error);
    // If error and asking for default, return default
    if (deploymentSite === DEFAULT_DEPLOYMENT.site) {
      return DEFAULT_DEPLOYMENT;
    }
    return null;
  }
}

// Export all functions and constants
export {
  DEFAULT_DEPLOYMENT
};